import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  FormControlLabel,
  Checkbox,
  Alert
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Home, TrendingUp, Calculate, FileDownload, Group } from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useCurrentUser } from '../../../hooks/useApi';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useQuery } from '@tanstack/react-query';

interface WeightingProfile {
  id: number;
  name: string;
  description: string;
  is_active: boolean;
}

interface MarketRanking {
  market_id: number;
  market_name: string;
  market_score: number;
  market_rank: number;
  market_percentile: number;
  covid_score: number;
  // Field rankings (optional - only present when includeFieldRankings is true)
  inventory_rooms_rank?: number;
  [key: string]: any; // Allow for dynamic field ranking columns
}

interface Continent {
  id: number;
  name: string;
}

interface Country {
  id: number;
  name: string;
}

interface MarketGrouping {
  id: number;
  name: string;
}

export const MarketRankings: React.FC = () => {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();

  // Form state
  const [dataSetType, setDataSetType] = useState<string>('world');
  const [dataSetId, setDataSetId] = useState<number | ''>('');
  const [weightingProfileId, setWeightingProfileId] = useState<number | ''>('');
  const [sliceValue, setSliceValue] = useState<string>('All');
  const [includeFieldRankings, setIncludeFieldRankings] = useState<boolean>(false);
  const [period, setPeriod] = useState<string>('');

  // Results state
  const [rankings, setRankings] = useState<MarketRanking[]>([]);
  const [isCalculating, setIsCalculating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Fetch weighting profiles
  const { data: profilesData } = useQuery({
    queryKey: ['weighting-profiles'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/marketcompass/admin/weighting-profiles-list');
      if (!response || !response.ok) throw new Error('Failed to fetch weighting profiles');
      return response.json();
    },
    enabled: !!user
  });

  // Fetch continents
  const { data: continentsData } = useQuery({
    queryKey: ['continents'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/store/markets/continents');
      if (!response || !response.ok) throw new Error('Failed to fetch continents');
      return response.json();
    },
    enabled: !!user && dataSetType === 'continent'
  });

  // Fetch countries
  const { data: countriesData } = useQuery({
    queryKey: ['countries'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/store/markets/countries');
      if (!response || !response.ok) throw new Error('Failed to fetch countries');
      return response.json();
    },
    enabled: !!user && dataSetType === 'country'
  });

  // Fetch market groupings
  const { data: groupingsData } = useQuery({
    queryKey: ['market-groupings'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/marketcompass/admin/market-groupings/list');
      if (!response || !response.ok) throw new Error('Failed to fetch market groupings');
      return response.json();
    },
    enabled: !!user && dataSetType === 'grouping'
  });

  // Filter for active profiles only in the dropdown
  const allProfiles: WeightingProfile[] = profilesData?.data || [];
  const weightingProfiles = allProfiles.filter(p => p.is_active);
  const continents: Continent[] = continentsData?.data || [];
  const allCountries: Country[] = countriesData?.data || [];
  const groupings: MarketGrouping[] = groupingsData?.groupings || [];

  // Sort countries with United States first, then alphabetically
  const countries = React.useMemo(() => {
    const unitedStates = allCountries.find(c => c.name === 'United States');
    const otherCountries = allCountries
      .filter(c => c.name !== 'United States')
      .sort((a, b) => a.name.localeCompare(b.name));

    return unitedStates ? [unitedStates, ...otherCountries] : otherCountries;
  }, [allCountries]);

  // Auto-select first weighting profile when profiles load
  useEffect(() => {
    if (weightingProfiles.length > 0 && !weightingProfileId) {
      setWeightingProfileId(weightingProfiles[0].id);
    }
  }, [weightingProfiles, weightingProfileId]);

  const sliceOptions = [
    'All',
    'Luxury',
    'Upper Upscale',
    'Upscale',
    'Upper Midscale',
    'Midscale',
    'Economy'
  ];

  const handleCalculate = async () => {
    // Validate required fields
    if (!weightingProfileId) {
      setError('Please select a weighting profile');
      return;
    }

    if (dataSetType !== 'world' && !dataSetId) {
      setError(`Please select a ${dataSetType}`);
      return;
    }

    setIsCalculating(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await authenticatedFetch(
        '/api/marketcompass/admin/market-rankings/calculate',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            dataSetType,
            dataSetId: dataSetType === 'world' ? undefined : dataSetId,
            weightingProfileId,
            sliceValue,
            includeFieldRankings,
            period: period || undefined
          })
        }
      );

      if (!response) {
        throw new Error('No response from server');
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to calculate rankings');
      }

      const result = await response.json();
      setRankings(result.data || []);
      setSuccess(`Successfully calculated rankings for ${result.data?.length || 0} markets`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsCalculating(false);
    }
  };

  const handleExportToExcel = () => {
    if (rankings.length === 0) return;

    // Prepare data for export
    const exportData = rankings.map(ranking => {
      const row: any = {};
      columns.forEach(col => {
        const field = col.field;
        const headerName = col.headerName || field;
        let value = ranking[field];

        // Format values based on column formatter
        if (col.valueFormatter && value !== null && value !== undefined) {
          value = (col.valueFormatter as any)(value);
        }

        row[headerName] = value;
      });
      return row;
    });

    // Convert to CSV
    const headers = columns.map(col => col.headerName || col.field).join(',');
    const rows = exportData.map(row =>
      columns.map(col => {
        const value = row[col.headerName || col.field];
        // Escape values that contain commas or quotes
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          return `"${value.replace(/"/g, '""')}"`;
        }
        return value ?? '';
      }).join(',')
    ).join('\n');

    const csv = `${headers}\n${rows}`;

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().split('T')[0];
    const datasetName = dataSetType === 'world' ? 'World' :
      dataSetType === 'continent' ? continents.find(c => c.id === dataSetId)?.name :
      dataSetType === 'country' ? countries.find(c => c.id === dataSetId)?.name :
      groupings.find(g => g.id === dataSetId)?.name;
    const filename = `Market_Rankings_${datasetName}_${sliceValue}_${timestamp}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Show loading spinner while user data is being fetched
  if (isLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Redirect if user doesn't have admin permission
  if (!user?.permissions?.includes('market_compass.admin')) {
    return <Navigate to="/" replace />;
  }

  // Base columns always shown
  const baseColumns: GridColDef[] = [
    {
      field: 'market_rank',
      headerName: 'Rank',
      width: 100,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'market_name',
      headerName: 'Market Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'market_score',
      headerName: 'Score',
      width: 150,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null | undefined) => {
        return value?.toFixed(2) || '0.00';
      }
    },
    {
      field: 'market_percentile',
      headerName: 'Percentile',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null | undefined) => {
        return `${value?.toFixed(2) || '0.00'}%`;
      }
    },
    {
      field: 'covid_score',
      headerName: 'COVID Score',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value: number | null | undefined) => {
        return value?.toFixed(4) || '0.0000';
      }
    }
  ];

  // Generate columns dynamically from the first ranking result when field rankings are included
  const columns: GridColDef[] = React.useMemo(() => {
    if (!includeFieldRankings || rankings.length === 0) {
      return baseColumns;
    }

    // Get all field ranking columns from the first result
    const firstRanking = rankings[0];
    const fieldRankingColumns: GridColDef[] = Object.keys(firstRanking)
      .filter(key => key.endsWith('_rank') && key !== 'market_rank')
      .map(key => ({
        field: key,
        headerName: key.replace(/_rank$/, '').replace(/_/g, ' ').toUpperCase(),
        width: 150,
        align: 'right' as const,
        headerAlign: 'right' as const,
        valueFormatter: (params: any) => {
          const value = params.value as number | null | undefined;
          return value?.toString() || '-';
        }
      }));

    return [...baseColumns, ...fieldRankingColumns];
  }, [includeFieldRankings, rankings]);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Market Rankings</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <TrendingUp sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Market Rankings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Calculate market performance rankings using weighted metrics
          </Typography>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Controls */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Dataset Type"
                value={dataSetType}
                onChange={(e) => {
                  setDataSetType(e.target.value);
                  setDataSetId('');
                }}
                required
              >
                <MenuItem value="world">World</MenuItem>
                <MenuItem value="continent">Continent</MenuItem>
                <MenuItem value="country">Country</MenuItem>
                <MenuItem value="grouping">Market Grouping</MenuItem>
              </TextField>
            </Grid>

            {dataSetType === 'continent' && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Continent"
                  value={dataSetId}
                  onChange={(e) => setDataSetId(Number(e.target.value))}
                  required
                >
                  {continents.map((continent) => (
                    <MenuItem key={continent.id} value={continent.id}>
                      {continent.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {dataSetType === 'country' && (
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  fullWidth
                  label="Country"
                  value={dataSetId}
                  onChange={(e) => setDataSetId(Number(e.target.value))}
                  required
                >
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>
                      {country.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {dataSetType === 'grouping' && (
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    select
                    fullWidth
                    label="Market Grouping"
                    value={dataSetId}
                    onChange={(e) => setDataSetId(Number(e.target.value))}
                    required
                  >
                    {groupings.map((grouping) => (
                      <MenuItem key={grouping.id} value={grouping.id}>
                        {grouping.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/admin/market-compass/market-rankings/market-groupings')}
                    sx={{ minWidth: 'auto', px: 2, py: 1.75 }}
                    title="Manage Market Groupings"
                  >
                    <Group />
                  </Button>
                </Box>
              </Grid>
            )}

            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  select
                  fullWidth
                  label="Weighting Profile"
                  value={weightingProfileId}
                  onChange={(e) => setWeightingProfileId(Number(e.target.value))}
                  required
                >
                  {weightingProfiles.map((profile) => (
                    <MenuItem key={profile.id} value={profile.id}>
                      {profile.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/market-compass/market-rankings/weighting-profiles')}
                  sx={{ minWidth: 'auto', px: 2, py: 1.75 }}
                  title="Manage Weighting Profiles"
                >
                  <Calculate />
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Hotel Class"
                value={sliceValue}
                onChange={(e) => setSliceValue(e.target.value)}
                required
              >
                {sliceOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Period (Optional)"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="Leave blank for most recent data"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeFieldRankings}
                    onChange={(e) => setIncludeFieldRankings(e.target.checked)}
                  />
                }
                label="Include Field Rankings (detailed metrics)"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                size="large"
                startIcon={isCalculating ? <CircularProgress size={20} /> : <Calculate />}
                onClick={handleCalculate}
                disabled={isCalculating || !weightingProfileId}
                fullWidth
              >
                {isCalculating ? 'Calculating...' : 'Calculate Rankings'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Results */}
        {rankings.length > 0 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Results ({rankings.length} markets)
              </Typography>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={handleExportToExcel}
              >
                Export to Excel
              </Button>
            </Box>
            <Paper sx={{ height: 600, width: '100%' }}>
              <DataGrid
                rows={rankings}
                columns={columns}
                getRowId={(row) => row.market_id.toString()}
                pageSizeOptions={[10, 25, 50, 100]}
                initialState={{
                  pagination: { paginationModel: { pageSize: 25 } }
                }}
                disableRowSelectionOnClick
              />
            </Paper>
          </>
        )}
      </Box>
    </Container>
  );
};
